'use client';

import DynamicImage from '@/components/commons/dynamic-image';
import ModalGiftReward from '@/components/sections/builder/elements/body/cart-total/component/modal-gift-reward';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { dummyPromotions } from '@/constant/dummyData/dummy-carts';
import useModal from '@/hooks/useModal';
import { cn, truncateText } from '@/lib/utils';
import { ThemeColorSchemeType } from '@/stores/builder';
import { usePromotionStore } from '@/stores/promotion';
import { BasePromotion } from '@/types/http/promotion/common';
import { GiftIcon, Trash2 } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';

type ListProductFreeGiftProps = {
  preview?: boolean;
  token?: string;
  colorScheme?: ThemeColorSchemeType;
  isLoading?: boolean;
};

export function ListProductFreeGift({
  preview,
  token,
  colorScheme,
  isLoading,
}: ListProductFreeGiftProps) {
  const modalGift = useModal();

  const [promoReward, setPromoReward] = useState<BasePromotion | null>(null);
  const [maxQtyReward, setMaxQtyReward] = useState<number>(0);
  const [selectedTab, setSelectedTab] = useState<'product_group' | 'product_variant'>(
    'product_group'
  );

  const { selectedPromotion, selectedFreeGift, removeFreeGiftByItemId, replaceFreeGift } =
    usePromotionStore(
      (s) => ({
        selectedPromotion: s.selectedPromotion,
        selectedFreeGift: s.selectedFreeGift,
        removeFreeGiftByItemId: s.removeFreeGiftByItemId,
        replaceFreeGift: s.replaceFreeGift,
      }),
      shallow
    );

  return (
    <>
      <div className='relative flex w-full flex-col'>
        <div className='mb-2 flex items-center gap-2' style={{ color: colorScheme?.text }}>
          <GiftIcon />
          <p className='text-sm md:text-base'>YOUR FREE GIFT</p>
        </div>
        {isLoading ? (
          <div className='flex flex-col gap-1.5'>
            <Skeleton className='h-20 w-full' />
          </div>
        ) : (
          <div className='flex w-full flex-col gap-2 rounded-xl border border-gray-200 p-3'>
            {(preview ? dummyPromotions : selectedPromotion).map((promo) => {
              const rewards = selectedFreeGift.filter(
                (gift) => gift.promotion_id === promo.promotion_id
              );

              return promo.product_reward && promo.product_reward.length > 0 ? (
                <div key={promo.promotion_id} className='relative flex flex-col gap-2'>
                  <div className='flex flex-col items-start justify-between md:flex-row md:items-center'>
                    <span
                      className='text-sm font-medium lg:text-base'
                      style={{
                        color: colorScheme?.text,
                      }}
                    >
                      {promo.promotion_name}
                    </span>
                    <Button
                      className={cn(
                        'p-1 text-xs font-normal transition-all lg:px-4',
                        'border border-[--btn-outline] bg-transparent text-[--btn-outline] hover:opacity-80'
                      )}
                      variant={null}
                      onClick={() => {
                        modalGift.open();
                        setPromoReward(promo as BasePromotion);
                        setMaxQtyReward(
                          promo.product_reward && promo.product_reward[0]
                            ? promo.counted_multiply
                              ? promo.product_reward[0].qty * promo.counted_multiply
                              : promo.product_reward[0].qty
                            : 0
                        );
                      }}
                      disabled={preview}
                    >
                      {rewards.length > 0 ? 'Change Gift' : 'Choose Gift'}
                    </Button>
                  </div>
                  {rewards.length > 0 &&
                    rewards.map((reward) =>
                      reward.items.map((item) => (
                        <div
                          key={item.item_id}
                          className='relative flex flex-row items-center justify-between gap-2'
                        >
                          <div className='flex min-w-0 gap-2'>
                            <DynamicImage
                              src={
                                (item.product_assets?.[0] as string) ||
                                String(reward.product_group.product_assets?.[0])
                              }
                              alt={item.product_name as string}
                              width={500}
                              height={500}
                              className='aspect-square h-[70px] w-[70px] rounded border object-contain'
                            />

                            <div className='flex w-full min-w-0 flex-col gap-1 overflow-hidden text-sm font-normal text-gray-600'>
                              <span
                                className='line-clamp-1 break-words'
                                title={`${reward.product_group.product_name} ${
                                  item.is_variants ? `- ${item.item_name}` : ''
                                }`}
                              >
                                {truncateText(reward.product_group.product_name as string, 30)}{' '}
                                {item?.is_variants &&
                                  `- ${truncateText(item.item_name as string, 25)}`}
                              </span>
                              <div className='flex items-center'>
                                <Link
                                  href={`/products/${item.slug}`}
                                  className='text-xs text-primary underline'
                                  target='_blank'
                                >
                                  View Product
                                </Link>
                              </div>
                              <div className='flex items-center gap-1 lg:hidden'>
                                <span>x{item.qty}</span>
                                <Button
                                  type='button'
                                  variant='secondary'
                                  size='icon'
                                  className='text-xs text-red-500 hover:text-red-700'
                                  onClick={() => {
                                    removeFreeGiftByItemId(
                                      reward.promotion_id as string,
                                      item.item_id
                                    );
                                  }}
                                >
                                  <Trash2 className='size-4' />
                                </Button>
                              </div>
                            </div>
                          </div>

                          <div className='hidden items-center gap-3 lg:flex'>
                            <span>x{item.qty}</span>
                            <Button
                              type='button'
                              variant='secondary'
                              size='icon'
                              className='text-xs text-red-500 hover:text-red-700'
                              onClick={() => {
                                removeFreeGiftByItemId(reward.promotion_id as string, item.item_id);
                              }}
                            >
                              <Trash2 className='size-4' />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                </div>
              ) : null;
            })}
            {(selectedPromotion.some(
              (promo) => !selectedFreeGift.find((gift) => gift.promotion_id === promo.promotion_id)
            ) ||
              preview) && <span>Don&apos;t want free gift? Feel free to skip.</span>}
          </div>
        )}
      </div>

      <ModalGiftReward
        openModal={modalGift.isShow}
        promotion={promoReward as BasePromotion}
        token={token as string}
        maxQty={maxQtyReward}
        setOpenModal={modalGift.setIsShow}
        setPromotion={setPromoReward}
        onSaveGift={(data) => {
          replaceFreeGift(promoReward?.promotion_id as string, data);
        }}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        colorScheme={colorScheme}
        initialSelectedProducts={selectedFreeGift.filter(
          (gift) => gift.promotion_id === promoReward?.promotion_id
        )}
      />
    </>
  );
}
